import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  TablePagination,
  useTheme,
  alpha,
  Card,
  Stack,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import 'dayjs/locale/th';
import _ from 'lodash';

dayjs.extend(buddhistEra);
dayjs.locale('th');

export default function SalaryListTable({
  salary = [],
  handleDelete,
  handleDetail,
  onPrint,
  handleOpenReport,
}) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Mock data หากไม่มีข้อมูล
  const mockSalaryData = [];

  const salaryData = salary.length > 0 ? salary : mockSalaryData;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  };

  // พนักงานรายเดือน (FULLTIME) จ่ายทั้งเดือน → ไม่มีงวด
  // พนักงานรายวัน (DAILY) จ่าย 2 งวด/เดือน → ต่อท้ายด้วยงวดที่ 1 (1-15) / งวดที่ 2 (16-สิ้นเดือน)
  // ดูจากวันที่เริ่มงวด (dateStart) — ห้ามใช้ dateEnd เพราะเก็บเป็น 23:59:59.999Z (UTC) พอแปลง
  // เป็น local time ประเทศไทย (+7) จะข้ามเที่ยงคืนกลายเป็นวันถัดไป ทำให้เช็ค <=15 ผิดงวด
  const formatPayPeriodLabel = (row) => {
    const dateLabel = dayjs(row?.dateStart).format('D MMMM BB');
    if (row?.role_type === 'FULLTIME') {
      return `ค่าแรงเดือน ${dateLabel}`;
    }
    const period = dayjs(row?.dateStart).date() <= 15 ? 'งวดที่ 1' : 'งวดที่ 2';
    return `ค่าแรงเดือน ${dateLabel} ${period}`;
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      draft: { label: 'ร่าง', color: 'default' },
      pending: { label: 'รอการอนุมัติ', color: 'warning' },
      approved: { label: 'อนุมัติแล้ว', color: 'info' },
      paid: { label: 'จ่ายแล้ว', color: 'success' },
      cancelled: { label: 'ยกเลิก', color: 'error' },
    };

    const config = statusConfig[status] || {
      label: 'ไม่ทราบ',
      color: 'default',
    };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = salaryData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (salaryData.length === 0) {
    return (
      <Card variant="outlined">
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            ไม่พบข้อมูลเงินเดือน
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ยังไม่มีการบันทึกข้อมูลเงินเดือนในระบบ
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                <TableCell
                  sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 80 }}
                >
                  ลำดับ
                </TableCell>
                <TableCell
                  sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 80 }}
                >
                  รหัสรายการ
                </TableCell>
                <TableCell
                  sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 200 }}
                >
                  ช่วงที่จ่าย
                </TableCell>

                <TableCell
                  align="right"
                  sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 140 }}
                >
                  ค่าใช้จ่ายรวม
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 140 }}
                >
                  ประเภทพนักงาน
                </TableCell>
                {/* <TableCell
                  align="center"
                  sx={{ fontWeight: 600, minWidth: 120 }}
                >
                  สถานะเอกสาร
                </TableCell> */}
                <TableCell
                  align="center"
                  sx={{ color: '#FFFFFF', fontWeight: 600, minWidth: 420 }}
                >
                  จัดการ
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={row._id}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  {/* ลำดับ */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {page * rowsPerPage + index + 1}
                    </Typography>
                  </TableCell>

                  {/* รหัสรายการ */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row?.code}
                    </Typography>
                  </TableCell>

                  {/* ช่วงที่จ่าย */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formatPayPeriodLabel(row)}
                      </Typography>
                      {row.documentNumber && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          เลขที่: {row.documentNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* ค่าใช้จ่ายรวม */}
                  <TableCell align="right">
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="primary.main"
                    >
                      {formatCurrency(_.sumBy(row.salaryData, (o) => o?.total))}
                    </Typography>
                  </TableCell>
                  {/* ประเภทพนักงาน */}
                  <TableCell align="right">
                    <Typography variant="body1">
                      {row.role_type === 'FULLTIME'
                        ? 'พนักงานรายเดือน'
                        : 'พนักงานรายวัน'}
                    </Typography>
                  </TableCell>

                  {/* สถานะเอกสาร
                  <TableCell align="center">
                    {getStatusChip(row.status)}
                  </TableCell> */}

                  {/* ปุ่มจัดการ */}
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.75}
                      justifyContent="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<ViewIcon fontSize="small" />}
                        onClick={() => handleDetail(row._id)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        ดูรายละเอียด
                      </Button>

                      {handleOpenReport && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<AccessTimeIcon fontSize="small" />}
                          onClick={() => handleOpenReport(row)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          ตรวจสอบเวลาเข้างาน
                        </Button>
                      )}

                      {/* <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<EditIcon fontSize="small" />}
                        disabled={
                          row.status === 'paid' || row.status === 'cancelled'
                        }
                      >
                        แก้ไข
                      </Button> */}

                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        startIcon={<PrintIcon fontSize="small" />}
                        onClick={() => onPrint(row)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        พิมพ์
                      </Button>

                      {/* <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<DownloadIcon fontSize="small" />}
                      >
                        ดาวน์โหลด
                      </Button> */}

                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        disabled={row.status === 'paid'}
                        onClick={() => handleDelete(row._id)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        ลบ
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={salaryData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="แสดงต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`
          }
        />
      </Card>
    </Box>
  );
}
